import { useRef } from "react";
import { validateImageFile } from "@/lib/file-utils";
import type { ValidationResult } from "@/types/validation";
import Stepper from "../ui/stepper";
import UploadStep from "./steps/UploadStep";
import PreviewStep from "./steps/PreviewStep";
import ValidationStep from "./steps/ValidationStep";
import SuccessStep from "./steps/SuccessStep";
import ErrorStep from "./steps/ErrorStep";

interface PhotoUploaderProps {
  selectedFile: File | null;
  onFileSelect: (file: File) => void;
  onRemoveFile: () => void;
  onValidatePhoto: () => void;
  isValidating: boolean;
  validationResult?: ValidationResult | null;
  currentStep: number;
  onReset: () => void;
  documentLabel: string;
}

const STEPS = [
  { id: 1, label: "Upload" },
  { id: 2, label: "Preview" },
  { id: 3, label: "Validate" },
  { id: 4, label: "Results" },
];

export default function PhotoUploader({
  selectedFile,
  onFileSelect,
  onRemoveFile,
  onValidatePhoto,
  isValidating,
  validationResult,
  currentStep,
  onReset,
  documentLabel,
}: PhotoUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const validation = validateImageFile(files[0]);
      if (!validation.isValid) return;
      onFileSelect(files[0]);
    }
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="bg-card rounded-lg border border-border shadow-sm overflow-hidden">
        <div className="px-4 sm:px-6 lg:px-8 py-8 border-b border-border/50 bg-gradient-to-b from-muted to-card">
          <Stepper steps={STEPS} currentStep={currentStep} />
        </div>

        {currentStep === 1 && (
          <UploadStep
            documentLabel={documentLabel}
            onFileSelect={onFileSelect}
            fileInputRef={fileInputRef}
            handleDragOver={handleDragOver}
            handleDragLeave={handleDragLeave}
            handleDrop={handleDrop}
            openFileDialog={openFileDialog}
          />
        )}

        {currentStep === 2 && selectedFile && (
          <PreviewStep
            documentLabel={documentLabel}
            file={selectedFile}
            onRemoveFile={onRemoveFile}
            onValidate={onValidatePhoto}
            isValidating={isValidating}
          />
        )}

        {currentStep === 3 && selectedFile && (
          <ValidationStep file={selectedFile} />
        )}

        {currentStep === 4 && validationResult && (
          <>
            {validationResult.status === "success" ? (
              <SuccessStep documentLabel={documentLabel} result={validationResult} onUploadNew={onReset} />
            ) : (
              <ErrorStep result={validationResult} onTryAgain={onReset} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
